import { Controller } from '@hotwired/stimulus'

/**
 * TooltipController
 *
 * Purpose
 * -------
 * Provides a lightweight, dependency-free tooltip / popover interaction that:
 * - Opens on hover and keyboard focus
 * - Remains open while the pointer is over either the trigger OR the popover
 * - Closes on mouseleave / focus leaving the tooltip root (with a small delay to prevent flicker)
 * - Closes on `Escape`
 * - Closes when clicking anywhere outside of the tooltip root
 *
 * This controller intentionally does NOT manage tooltip presentation directly (e.g. `display: none`).
 * Instead it toggles `data-open="true|false"` on the controller root element; CSS is responsible for
 * visibility, transitions, and pointer-event behavior.
 *
 * -----------------------
 * Targets
 * -----------------------
 * triggerTarget (required)
 * - The element the user hovers/focuses to open the tooltip.
 * - Typically a <div> containing inline text.
 * - Should be focusable for keyboard users (e.g. `tabindex="0"`).
 *
 * popoverTarget (required)
 * - The tooltip content container that becomes visible when `data-open="true"`.
 * - Must live inside the controller root so the user can move from trigger -> popover without closing.
 * - Can contain interactive elements (buttons, links, etc.).
 *
 * -----------------------
 * Expected CSS behavior
 * -----------------------
 * This controller only toggles `data-open`.
 * Your CSS should implement visibility and interactivity
 *
 * -----------------------
 * Actions (recommended)
 * -----------------------
 * Attach actions to the controller root so hover transitions between trigger/popover
 * remain within the root element:
 *
 *   data-action="
 *     mouseenter->tooltip#open
 *     mouseleave->tooltip#scheduleClose
 *     focusin->tooltip#open
 *     focusout->tooltip#onFocusOut
 *     keydown->tooltip#onKeyDown
 *   "
 *
 * Notes:
 * - `mouseenter` / `mouseleave` handle mouse open/close behavior.
 * - `focusin` / `focusout` ensure keyboard accessibility.
 * - `keydown` listens for Escape to close.

 */
export default class extends Controller {
  static targets = ['trigger', 'popover']

  /**
   * Lifecycle: connect()
   * - Initializes internal timer state
   * - Binds the document event handler to preserve `this`
   * - Sets initial open state to false
   * - Registers a click-outside listener on `document`
   */
  connect() {
    this.closeTimer = null
    this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this)

    this.setOpen(false)

    document.addEventListener('mousedown', this.onDocumentMouseDown)
  }

  /**
   * Lifecycle: disconnect()
   * - Removes the document event handler to prevent memory leaks
   * - Clears any pending close timer
   */
  disconnect() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown)
    this.clearCloseTimer()
  }

  /**
   * Action: open()
   * - Immediately opens the tooltip
   * - Cancels any pending scheduled close (prevents closing right after re-enter)
   */
  open() {
    this.clearCloseTimer()
    this.setOpen(true)
  }

  /**
   * Clears any pending scheduled close.
   * This ensures only one close timer can exist at a time.
   */
  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }

  /**
   * Action: scheduleClose()
   * - Schedules the tooltip to close after a short delay
   * - The delay prevents flicker when moving between trigger and popover
   */
  scheduleClose() {
    this.clearCloseTimer()
    this.closeTimer = setTimeout(() => this.setOpen(false), 250)
  }

  /**
   * Action: onFocusOut(event)
   * - If focus leaves the controller root entirely, schedule a close
   * - If focus remains within the root (e.g., moving from trigger to a button inside popover),
   *   the tooltip stays open
   */
  onFocusOut(event) {
    if (!this.element.contains(event.relatedTarget)) {
      this.scheduleClose()
    }
  }

  /**
   * Action: onKeyDown(event)
   * - Closes the tooltip on Escape
   * - Stops propagation so parent handlers do not also react to Escape
   * - Returns focus to the trigger for a predictable keyboard experience
   */
  onKeyDown(event) {
    if (event.key !== 'Escape') return
    event.stopPropagation()
    this.setOpen(false)
    if (this.hasTriggerTarget) this.triggerTarget.focus()
  }

  /**
   * Internal: setOpen(isOpen)
   * - Updates the root `data-open` attribute that CSS uses to show/hide the popover
   * - Also keeps `aria-expanded` in sync when a trigger target exists
   */
  setOpen(isOpen) {
    this.element.dataset.open = String(isOpen)

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-expanded', String(isOpen))
    }
  }

  /**
   * Document handler: onDocumentMouseDown(event)
   * - Closes the tooltip if the user clicks anywhere outside the controller root
   * - This enables "click outside to dismiss" behavior
   */
  onDocumentMouseDown(event) {
    if (!this.element.contains(event.target)) {
      this.setOpen(false)
    }
  }
}
